import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import type { HistoryRoute } from "~/types";

export function useNavigationHistory() {
  const { historyStack, preRoute, isLocationChanged } =
    storeToRefs(useGlobalStore());
  const businessStore = useBusinessStore();
  const { currentStep, selectedLocation, bookingSource, selectedEmployee } =
    storeToRefs(businessStore);
  const route = useRoute();
  const localePath = useLocalePath();
  const { locales } = useI18n();

  const storefrontSegments = (path: string) => {
    const segments = path.split("/").filter(Boolean);
    const localeCodes = [...locales.value].map((locale) =>
      typeof locale === "string" ? locale : locale.code,
    );

    return localeCodes.includes(segments[0]) ? segments.slice(1) : segments;
  };

  const menuRoutes = new Set([
    "memberships",
    "services",
    "products",
    "gift-cards",
    "staff",
    "about-us",
    "finished",
  ]);

  const addToHistory = (newRoute: HistoryRoute) => {
    const lastPath = historyStack.value[historyStack.value.length - 1];
    if (!lastPath || lastPath.path !== newRoute.path) {
      historyStack.value.push(newRoute);
    }
  };

  const resetHistory = (newRoute: HistoryRoute) =>
    historyStack.value.splice(0, historyStack.value.length, newRoute);

  const goBack = async () => {
    const pathSegments = storefrontSegments(route.path);
    const identifier =
      selectedLocation.value?.identifier ||
      route.params.location_identifier ||
      pathSegments[0];

    // 1. Specific step-based logic (e.g. Gift Cards steps)
    if (
      route.path.includes("/gift-cards") &&
      currentStep.value === "select_recipient"
    ) {
      currentStep.value = "choose_amount";
      return;
    }

    // 2. Explicit Page-to-Page Mappings
    const path = route.path;

    // Booking Flow
    if (path.includes("/booking/checkout")) {
      await navigateTo(
        localePath(`/${identifier}/booking/review-and-confirm`),
        {
          replace: true,
        },
      );
      return;
    }
    if (path.includes("/booking/review-and-confirm")) {
      if (bookingSource.value === "per_service") {
        await navigateTo(
          localePath(
            `/${identifier}/booking/times?source=employee&per_service=1`,
          ),
          {
            replace: true,
          },
        );
        return;
      }
      const employeeQuery = selectedEmployee.value?.id
        ? `?source=employee&employee_id=${selectedEmployee.value.id}`
        : "?source=employee";
      await navigateTo(
        localePath(`/${identifier}/booking/times${employeeQuery}`),
        {
          replace: true,
        },
      );
      return;
    }
    if (path.includes("/booking/times")) {
      if (
        bookingSource.value === "per_service" ||
        route.query.per_service === "1"
      ) {
        await navigateTo(localePath(`/${identifier}/booking/employees`), {
          replace: true,
        });
        return;
      }
      const isEmployeeFlow =
        route.query.source === "employee" || !!route.query.employee_id;
      if (isEmployeeFlow) {
        await navigateTo(localePath(`/${identifier}/booking/employees`), {
          replace: true,
        });
        return;
      }
      await navigateTo(localePath(`/${identifier}/booking/services`), {
        replace: true,
      });
      return;
    }
    if (path.includes("/booking/employees")) {
      /**
       * Entering from a professional's profile skips the professionals step —
       * the choice was already made. Stepping back past that step ends the
       * shortcut: from the services list the client walks the flow in full,
       * services then professionals then times, so continuing must land on the
       * professionals again rather than jumping to the times.
       */
      if (bookingSource.value === "employee") {
        bookingSource.value = "no_employee";
      }
      await navigateTo(localePath(`/${identifier}/booking/services`), {
        replace: true,
      });
      return;
    }
    if (path.includes("/booking/services")) {
      await navigateTo(localePath(`/${identifier}/`), { replace: true });
      return;
    }

    // Cart / Products Flow
    if (path.includes("/cart/checkout")) {
      await navigateTo(localePath(`/${identifier}/cart/review-and-confirm`), {
        replace: true,
      });
      return;
    }
    if (path.includes("/cart/review-and-confirm")) {
      await navigateTo(localePath(`/${identifier}/products`), {
        replace: true,
      });
      return;
    }

    if (route.params.id && path.includes("/products")) {
      await navigateTo(localePath(`/${identifier}/products`), {
        replace: true,
      });
      return;
    }

    if (path.includes("/products")) {
      await navigateTo(localePath(`/${identifier}/`), { replace: true });
      return;
    }

    // Memberships Flow
    if (path.includes("/memberships/checkout")) {
      await navigateTo(
        localePath(`/${identifier}/memberships/review-and-confirm`),
        {
          replace: true,
        },
      );
      return;
    }
    if (path.includes("/memberships/review-and-confirm")) {
      await navigateTo(localePath(`/${identifier}/memberships`), {
        replace: true,
      });
      return;
    }
    if (path.includes("/memberships")) {
      await navigateTo(localePath(`/${identifier}/`), { replace: true });
      return;
    }

    // Gift Cards Flow
    if (path.includes("/gift-cards/checkout")) {
      await navigateTo(
        localePath(`/${identifier}/gift-cards/review-and-confirm`),
        {
          replace: true,
        },
      );
      return;
    }
    if (path.includes("/gift-cards/review-and-confirm")) {
      await navigateTo(localePath(`/${identifier}/gift-cards`), {
        replace: true,
      });
      return;
    }
    if (path.includes("/gift-cards")) {
      await navigateTo(localePath(`/${identifier}/`), { replace: true });
      return;
    }

    // Top-level segments to Location Homepage (Staff, About-Us, etc.)
    const lastSegment = pathSegments[pathSegments.length - 1];
    const topLevelSegments = ["staff", "about-us"];

    if (topLevelSegments.includes(lastSegment)) {
      await navigateTo(localePath(`/${identifier}/`), { replace: true });
      return;
    }

    // 4. Fallback for nested routes (generic parent navigation)
    if (pathSegments.length > 1) {
      const parentPath = `/${pathSegments.slice(0, -1).join("/")}`;
      await navigateTo(localePath(parentPath), { replace: true });
      return;
    }

    // 5. Final fallback to root
    await navigateTo(
      localePath(pathSegments.length === 1 ? `/${pathSegments[0]}/` : "/"),
      { replace: true },
    );
  };

  const changeSelectedLocation = (
    newLocation: string | number,
    oldLocation: string | number,
  ) => {
    isLocationChanged.value = true;
    historyStack.value = historyStack.value.map((route) => {
      if (route.path.includes(`/${oldLocation}/`)) {
        return {
          ...route,
          path: route.path.replace(`/${oldLocation}/`, `/${newLocation}/`),
        };
      }
      return route;
    });
  };

  const canGoBack = computed(() => {
    // Show back button if we are deeper than the location root page
    return storefrontSegments(route.path).length > 1;
  });

  const checkIfCanRefresh = () => {
    // 1. Identify previous route segment
    const previousRouteName = preRoute.value?.name || "";

    // 2. Define Refresh Rules
    // Key: Module to possibly refresh
    // cases: list of previous segments that should PREVENT refresh
    // condition: optional extra condition (like location change check)
    const refreshRules: Record<
      string,
      { cases: string[]; condition?: boolean }
    > = {
      services: {
        cases: [
          "times",
          "employees",
          "confirm",
          "checkout",
          "location_identifier",
        ],
        condition: isLocationChanged.value === false,
      },
      memberships: {
        cases: ["confirm", "checkout", "location_identifier"],
        condition: isLocationChanged.value === false,
      },
      giftCards: {
        cases: ["confirm", "checkout", "location_identifier"],
        condition: isLocationChanged.value === false,
      },
      times: {
        cases: ["confirm"],
      },
      confirm: {
        cases: ["checkout"],
      },
    };

    // 3. Apply Rules
    Object.entries(refreshRules).forEach(([module, rule]) => {
      const isBlacklisted = rule.cases.includes(previousRouteName);
      const conditionMet = rule.condition ?? true;

      // We should REFRESH if:
      // - Segment is NOT blacklisted OR
      // - The specific condition (like location change) is NOT met (meaning we must refresh)
      const shouldRefresh = !isBlacklisted || !conditionMet;

      const storeKey = module as
        | "services"
        | "memberships"
        | "times"
        | "confirm"
        | "giftCards";
      businessStore.setRefreshStatus(storeKey, shouldRefresh);
    });

    isLocationChanged.value = false;
  };

  watch(
    route,
    (newRoute, oldRoute) => {
      if (oldRoute) {
        preRoute.value = {
          path: oldRoute.fullPath.split("?")[0],
          name: (oldRoute.name as string)
            ?.split("-")
            .slice(-1)[0]
            ?.split("___")[0],
        };
      }

      checkIfCanRefresh();

      const lastSegment = newRoute.path.split("/").pop() || "none";
      if (menuRoutes.has(lastSegment)) {
        resetHistory({
          path: newRoute.fullPath.split("?")[0],
          name: (newRoute.name as string)
            .split("-")
            .slice(-1)[0]
            .split("___")[0],
        });
      } else {
        addToHistory({
          path: newRoute.fullPath.split("?")[0],
          name: (newRoute.name as string)
            .split("-")
            .slice(-1)[0]
            .split("___")[0],
        });
      }
    },
    { immediate: true },
  );

  return {
    historyStack,
    canGoBack,
    addToHistory,
    goBack,
    resetHistory,
    changeSelectedLocation,
  };
}
